import { supabaseAdmin } from '../lib/supabaseClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ExpenseCategory {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
}

export interface ExpenseRecord {
  id: string;
  restaurantId: string;
  restaurantName: string;
  categoryId: string;
  categoryName: string;
  description: string;
  amount: number;
  kdvRate: number;
  kdvAmount: number;
  netAmount: number;
  expenseDate: string;
  receiptNote: string | null;
  receiptId: string | null;
  createdAt: string;
}

export interface ExpenseReceiptRecord {
  id: string;
  restaurantId: string;
  restaurantName: string;
  receiptDate: string;
  supplier: string | null;
  note: string | null;
  totalAmount: number;
  totalKdv: number;
  itemCount: number;
  items: ExpenseRecord[];
  createdAt: string;
}

export interface BatchExpenseItem {
  categoryId: string;
  description: string;
  amount: number;
  kdvRate: number;
}

export interface CreateBatchExpenseInput {
  restaurantId: string;
  receiptDate: string;
  supplier?: string;
  note?: string;
  items: BatchExpenseItem[];
}

export interface IncomeRecord {
  id: string;
  restaurantId: string;
  restaurantName: string;
  incomeDate: string;
  source: string;
  paymentMethod: string;
  amount: number;
  description: string | null;
  createdAt: string;
}

export interface CreateExpenseInput {
  restaurantId: string;
  categoryId: string;
  description: string;
  amount: number;
  kdvRate: number;
  expenseDate: string;
  receiptNote?: string;
}

export interface CreateIncomeInput {
  restaurantId: string;
  incomeDate: string;
  source: string;
  paymentMethod: string;
  amount: number;
  description?: string;
}

export interface AccountingPeriodSummary {
  totalExpenses: number;
  totalKdv: number;
  totalExpensesNet: number;
  totalIncome: number;
  netProfit: number;
  expensesByCategory: { category: string; total: number }[];
  incomeBySource: { source: string; total: number }[];
  incomeByPaymentMethod: { method: string; total: number }[];
  dailyBreakdown: {
    date: string;
    expenses: number;
    income: number;
  }[];
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const SOURCE_LABELS: Record<string, string> = {
  app: 'Lido App',
  yemeksepeti: 'Yemeksepeti',
  getir: 'Getir',
  trendyol: 'Trendyol Go',
  telefon: 'Telefon',
  diger: 'Diğer',
};

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  nakit: 'Nakit',
  kredi_karti: 'Kredi Kartı',
  online: 'Online',
  ys_vale: 'YS Vale',
};

// ---------------------------------------------------------------------------
// KDV helper
// ---------------------------------------------------------------------------

export function computeKdv(amountWithKdv: number, kdvRate: number) {
  if (kdvRate <= 0) return { kdvAmount: 0, netAmount: amountWithKdv };
  const kdvAmount = Math.round((amountWithKdv * kdvRate) / (100 + kdvRate) * 100) / 100;
  return { kdvAmount, netAmount: Math.round((amountWithKdv - kdvAmount) * 100) / 100 };
}

// ---------------------------------------------------------------------------
// Expense Categories
// ---------------------------------------------------------------------------

export async function listExpenseCategories(): Promise<ExpenseCategory[]> {
  const { data, error } = await supabaseAdmin
    .from('accounting_expense_categories')
    .select('id, name, description, sort_order')
    .order('sort_order', { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row: Record<string, unknown>) => ({
    id: row.id as string,
    name: row.name as string,
    description: (row.description as string) ?? null,
    sortOrder: Number(row.sort_order ?? 0),
  }));
}

export async function createExpenseCategory(name: string, description?: string): Promise<void> {
  const { error } = await supabaseAdmin
    .from('accounting_expense_categories')
    .insert({ name, description: description ?? null, sort_order: 50 });
  if (error) throw error;
}

export async function deleteExpenseCategory(id: string): Promise<void> {
  const { error } = await supabaseAdmin
    .from('accounting_expense_categories')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Expenses CRUD
// ---------------------------------------------------------------------------

export async function listExpenses(
  restaurantId?: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<ExpenseRecord[]> {
  let query = supabaseAdmin
    .from('accounting_expenses')
    .select(`
      id, restaurant_id, category_id, description, amount,
      kdv_rate, kdv_amount, expense_date,
      receipt_note, receipt_id, created_at,
      restaurants ( name ),
      accounting_expense_categories ( name )
    `)
    .order('expense_date', { ascending: false })
    .limit(500);

  if (restaurantId) query = query.eq('restaurant_id', restaurantId);
  if (dateFrom) query = query.gte('expense_date', dateFrom);
  if (dateTo) query = query.lte('expense_date', dateTo);

  const { data, error } = await query;
  if (error) throw error;

  return (data ?? []).map((row: Record<string, unknown>) => {
    const rest = row.restaurants as Record<string, unknown> | null;
    const cat = row.accounting_expense_categories as Record<string, unknown> | null;
    return {
      id: row.id as string,
      restaurantId: row.restaurant_id as string,
      restaurantName: (rest?.name as string) ?? 'Bilinmeyen',
      categoryId: row.category_id as string,
      categoryName: (cat?.name as string) ?? 'Bilinmeyen',
      description: row.description as string,
      amount: Number(row.amount ?? 0),
      kdvRate: Number(row.kdv_rate ?? 0),
      kdvAmount: Number(row.kdv_amount ?? 0),
      netAmount: Number(row.amount ?? 0) - Number(row.kdv_amount ?? 0),
      expenseDate: row.expense_date as string,
      receiptNote: (row.receipt_note as string) ?? null,
      receiptId: (row.receipt_id as string) ?? null,
      createdAt: row.created_at as string,
    };
  });
}

export async function createExpense(input: CreateExpenseInput): Promise<void> {
  const { kdvAmount } = computeKdv(input.amount, input.kdvRate);
  const { error } = await supabaseAdmin.from('accounting_expenses').insert({
    restaurant_id: input.restaurantId,
    category_id: input.categoryId,
    description: input.description,
    amount: input.amount,
    kdv_rate: input.kdvRate,
    kdv_amount: kdvAmount,
    expense_date: input.expenseDate,
    receipt_note: input.receiptNote ?? null,
  });
  if (error) throw error;
}

export async function deleteExpense(id: string): Promise<void> {
  const { error } = await supabaseAdmin
    .from('accounting_expenses')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Batch Expenses (Receipts)
// ---------------------------------------------------------------------------

export async function createBatchExpenses(input: CreateBatchExpenseInput): Promise<void> {
  // 1. Create receipt header
  const { data: receipt, error: receiptError } = await supabaseAdmin
    .from('accounting_expense_receipts')
    .insert({
      restaurant_id: input.restaurantId,
      receipt_date: input.receiptDate,
      supplier: input.supplier ?? null,
      note: input.note ?? null,
    })
    .select('id')
    .single();

  if (receiptError) throw receiptError;

  // 2. Insert all line items linked to the receipt
  const rows = input.items.map((item) => {
    const { kdvAmount } = computeKdv(item.amount, item.kdvRate);
    return {
      restaurant_id: input.restaurantId,
      category_id: item.categoryId,
      description: item.description,
      amount: item.amount,
      kdv_rate: item.kdvRate,
      kdv_amount: kdvAmount,
      expense_date: input.receiptDate,
      receipt_id: receipt.id,
    };
  });

  const { error: itemsError } = await supabaseAdmin
    .from('accounting_expenses')
    .insert(rows);

  if (itemsError) throw itemsError;
}

export async function listExpenseReceipts(
  restaurantId?: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<ExpenseReceiptRecord[]> {
  let query = supabaseAdmin
    .from('accounting_expense_receipts')
    .select(`
      id, restaurant_id, receipt_date, supplier, note, created_at,
      restaurants ( name ),
      accounting_expenses (
        id, category_id, description, amount,
        kdv_rate, kdv_amount, expense_date,
        receipt_note, receipt_id, created_at,
        accounting_expense_categories ( name )
      )
    `)
    .order('receipt_date', { ascending: false })
    .limit(200);

  if (restaurantId) query = query.eq('restaurant_id', restaurantId);
  if (dateFrom) query = query.gte('receipt_date', dateFrom);
  if (dateTo) query = query.lte('receipt_date', dateTo);

  const { data, error } = await query;
  if (error) throw error;

  return (data ?? []).map((row: Record<string, unknown>) => {
    const rest = row.restaurants as Record<string, unknown> | null;
    const items = (row.accounting_expenses as Record<string, unknown>[] | null) ?? [];
    const mapped = items.map((item) => {
      const cat = item.accounting_expense_categories as Record<string, unknown> | null;
      return {
        id: item.id as string,
        restaurantId: row.restaurant_id as string,
        restaurantName: (rest?.name as string) ?? 'Bilinmeyen',
        categoryId: item.category_id as string,
        categoryName: (cat?.name as string) ?? 'Bilinmeyen',
        description: item.description as string,
        amount: Number(item.amount ?? 0),
        kdvRate: Number(item.kdv_rate ?? 0),
        kdvAmount: Number(item.kdv_amount ?? 0),
        netAmount: Number(item.amount ?? 0) - Number(item.kdv_amount ?? 0),
        expenseDate: item.expense_date as string,
        receiptNote: (item.receipt_note as string) ?? null,
        receiptId: (item.receipt_id as string) ?? null,
        createdAt: item.created_at as string,
      };
    });
    const totalAmount = mapped.reduce((s, i) => s + i.amount, 0);
    const totalKdv = mapped.reduce((s, i) => s + i.kdvAmount, 0);
    return {
      id: row.id as string,
      restaurantId: row.restaurant_id as string,
      restaurantName: (rest?.name as string) ?? 'Bilinmeyen',
      receiptDate: row.receipt_date as string,
      supplier: (row.supplier as string) ?? null,
      note: (row.note as string) ?? null,
      totalAmount,
      totalKdv,
      itemCount: mapped.length,
      items: mapped,
      createdAt: row.created_at as string,
    };
  });
}

export async function deleteExpenseReceipt(id: string): Promise<void> {
  // CASCADE will delete linked expense items
  const { error } = await supabaseAdmin
    .from('accounting_expense_receipts')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Income CRUD
// ---------------------------------------------------------------------------

export async function listIncomeEntries(
  restaurantId?: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<IncomeRecord[]> {
  let query = supabaseAdmin
    .from('accounting_income_entries')
    .select(`
      id, restaurant_id, income_date, source, payment_method, amount,
      description, created_at,
      restaurants ( name )
    `)
    .order('income_date', { ascending: false })
    .limit(500);

  if (restaurantId) query = query.eq('restaurant_id', restaurantId);
  if (dateFrom) query = query.gte('income_date', dateFrom);
  if (dateTo) query = query.lte('income_date', dateTo);

  const { data, error } = await query;
  if (error) throw error;

  return (data ?? []).map((row: Record<string, unknown>) => {
    const rest = row.restaurants as Record<string, unknown> | null;
    return {
      id: row.id as string,
      restaurantId: row.restaurant_id as string,
      restaurantName: (rest?.name as string) ?? 'Bilinmeyen',
      incomeDate: row.income_date as string,
      source: row.source as string,
      paymentMethod: row.payment_method as string,
      amount: Number(row.amount ?? 0),
      description: (row.description as string) ?? null,
      createdAt: row.created_at as string,
    };
  });
}

export async function createIncomeEntry(input: CreateIncomeInput): Promise<void> {
  const { error } = await supabaseAdmin.from('accounting_income_entries').insert({
    restaurant_id: input.restaurantId,
    income_date: input.incomeDate,
    source: input.source,
    payment_method: input.paymentMethod,
    amount: input.amount,
    description: input.description ?? null,
  });
  if (error) throw error;
}

export async function deleteIncomeEntry(id: string): Promise<void> {
  const { error } = await supabaseAdmin
    .from('accounting_income_entries')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Summary / Statistics
// ---------------------------------------------------------------------------

export async function fetchAccountingSummary(
  restaurantId?: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<AccountingPeriodSummary> {
  const [expenses, income] = await Promise.all([
    listExpenses(restaurantId, dateFrom, dateTo),
    listIncomeEntries(restaurantId, dateFrom, dateTo),
  ]);

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const totalKdv = expenses.reduce((sum, e) => sum + e.kdvAmount, 0);
  const totalExpensesNet = totalExpenses - totalKdv;
  const totalIncome = income.reduce((sum, i) => sum + i.amount, 0);

  // Expenses by category
  const catMap = new Map<string, number>();
  expenses.forEach((e) => {
    catMap.set(e.categoryName, (catMap.get(e.categoryName) ?? 0) + e.amount);
  });
  const expensesByCategory = Array.from(catMap.entries())
    .map(([category, total]) => ({ category, total }))
    .sort((a, b) => b.total - a.total);

  // Income by source
  const srcMap = new Map<string, number>();
  income.forEach((i) => {
    const label = SOURCE_LABELS[i.source] ?? i.source;
    srcMap.set(label, (srcMap.get(label) ?? 0) + i.amount);
  });
  const incomeBySource = Array.from(srcMap.entries())
    .map(([source, total]) => ({ source, total }))
    .sort((a, b) => b.total - a.total);

  // Income by payment method
  const pmMap = new Map<string, number>();
  income.forEach((i) => {
    const label = PAYMENT_METHOD_LABELS[i.paymentMethod] ?? i.paymentMethod;
    pmMap.set(label, (pmMap.get(label) ?? 0) + i.amount);
  });
  const incomeByPaymentMethod = Array.from(pmMap.entries())
    .map(([method, total]) => ({ method, total }))
    .sort((a, b) => b.total - a.total);

  // Daily breakdown
  const dailyMap = new Map<string, { expenses: number; income: number }>();
  expenses.forEach((e) => {
    const existing = dailyMap.get(e.expenseDate) ?? { expenses: 0, income: 0 };
    existing.expenses += e.amount;
    dailyMap.set(e.expenseDate, existing);
  });
  income.forEach((i) => {
    const existing = dailyMap.get(i.incomeDate) ?? { expenses: 0, income: 0 };
    existing.income += i.amount;
    dailyMap.set(i.incomeDate, existing);
  });
  const dailyBreakdown = Array.from(dailyMap.entries())
    .map(([date, v]) => ({ date, ...v }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return {
    totalExpenses,
    totalKdv,
    totalExpensesNet,
    totalIncome,
    netProfit: totalIncome - totalExpenses,
    expensesByCategory,
    incomeBySource,
    incomeByPaymentMethod,
    dailyBreakdown,
  };
}
